// Light temporal summary derived from detected events/cycles.
//
// Phase 1 surfaces these basic, robust temporal parameters alongside the cycle
// overlay. The full categorical feature set + scoring lands in Phase 2; nothing
// here reports clinical angles.

import { mean } from "./signal";
import { cycleDurationCV, type EventResult, type GaitCycle } from "./events";
import type { Side } from "../pose/landmarks";

export interface SideTemporal {
  cycleCount: number;
  meanStancePct: number | null;
  meanSwingPct: number | null;
  meanCycleMs: number | null;
}

export interface TemporalSummary {
  stepCount: number;
  cadenceStepsPerMin: number | null;
  cycleConsistencyCV: number; // lower = more consistent
  left: SideTemporal;
  right: SideTemporal;
  /** Symmetry index of stance% (0 = perfectly symmetric, higher = more asym). */
  stanceSymmetryIndex: number | null;
  /** Which side shows the longer stance (potential affected side), or null. */
  longerStanceSide: Side | null;
}

export function summarizeTemporal(result: EventResult): TemporalSummary {
  const stepCount = result.events.filter((e) => e.type === "HS").length;

  const hsTimes = result.events
    .filter((e) => e.type === "HS")
    .map((e) => e.tMs)
    .sort((a, b) => a - b);
  const stepIntervals: number[] = [];
  for (let i = 1; i < hsTimes.length; i++) stepIntervals.push(hsTimes[i] - hsTimes[i - 1]);
  const meanStepMs = stepIntervals.length ? mean(stepIntervals) : null;
  const cadence = meanStepMs && meanStepMs > 0 ? 60000 / meanStepMs : null;

  const left = sideTemporal(result.cycles, "L");
  const right = sideTemporal(result.cycles, "R");

  let stanceSymmetryIndex: number | null = null;
  let longerStanceSide: Side | null = null;
  if (left.meanStancePct !== null && right.meanStancePct !== null) {
    const l = left.meanStancePct;
    const r = right.meanStancePct;
    const denom = 0.5 * (l + r);
    stanceSymmetryIndex = denom > 0 ? (Math.abs(l - r) / denom) * 100 : 0;
    longerStanceSide = l === r ? null : l > r ? "L" : "R";
  }

  return {
    stepCount,
    cadenceStepsPerMin: cadence,
    cycleConsistencyCV: cycleDurationCV(result.cycles),
    left,
    right,
    stanceSymmetryIndex,
    longerStanceSide,
  };
}

function sideTemporal(cycles: GaitCycle[], side: Side): SideTemporal {
  const cs = cycles.filter((c) => c.side === side);
  const stancePcts = cs.map((c) => c.stancePct).filter((v): v is number => v !== null);
  const swingPcts = stancePcts.map((s) => 100 - s);
  const durations = cs.map((c) => c.durationMs);
  return {
    cycleCount: cs.length,
    meanStancePct: stancePcts.length ? mean(stancePcts) : null,
    meanSwingPct: swingPcts.length ? mean(swingPcts) : null,
    meanCycleMs: durations.length ? mean(durations) : null,
  };
}
